import type { Practitioner, Reference } from '@medplum/fhirtypes'
import { formatTime } from '@seen/core/dates'
import type * as appointments from '@seen/core/models/appointments/index'
import type {
  EngineSlot,
  SchedulingRuleInfoMap,
} from '@seen/request-types/schedulingRules'
import { Button, cn } from '@seen/ui'
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronRight,
} from '@tabler/icons-react'
import { format, isSameMinute } from 'date-fns'
import { type JSX, useEffect, useRef, useState } from 'react'
import {
  gridMirroredSlots,
  partitionEngineSlots,
} from '#src/components/appointment/engineSlotTiers.ts'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#src/components/core/index.ts'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { Skeleton } from '#src/components/core/Skeleton.tsx'
import { useScrollIntoViewWhenSelected } from '#src/hooks/useScrollIntoViewWhenSelected.ts'

// Caps the scrollable list so a long candidate set can't push the rest of the
// form off-screen, without reserving that height when there is less to show —
// a two-line "no clean times" result should occupy two lines.
//
// Applied to BOTH the ScrollArea root and its viewport. ScrollArea defaults
// both to `size-full`; leaving either at `height: 100%` of an indefinite
// parent makes it grow to fit its content and never clip, so the list
// silently stops scrolling past the cap.
//
// The `!` is load-bearing: tailwind-merge does not treat `size-full` as
// conflicting with `h-*`, so both classes survive the merge and the winner
// would otherwise come down to stylesheet order. Same reason ScrollArea's own
// viewport classes use `!` to stop Radix children escaping their container.
const SLOTS_VIEWPORT_CLASS = 'h-auto! max-h-48'
// The loading skeleton keeps the old fixed height on purpose: reserving the
// space it is about to fill is what stops the form jumping when slots land.
const SLOTS_SKELETON_CLASS = 'h-48'

function slotKey(slot: EngineSlot): string {
  return `${slot.start}-${slot.end}-${slot.practitionerId}`
}

// A warning rule's human title, falling back to its code when the response
// carries no provenance entry for it.
function warningTitles(
  slot: EngineSlot,
  rules: SchedulingRuleInfoMap
): string[] {
  return slot.warnings.map((code) => rules[code]?.title ?? code)
}

function EngineRecommendedSlotsHeader(): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2">
      <p className="text-xs font-medium">Next Available</p>
    </div>
  )
}

export function EngineRecommendedSlotsSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <EngineRecommendedSlotsHeader />
      <div className={cn('flex flex-col gap-1.5 pr-3', SLOTS_SKELETON_CLASS)}>
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton
            // Static placeholder list; index keys are stable and never reorder.
            key={index}
            className="h-9 w-full"
          />
        ))}
      </div>
    </div>
  )
}

// A completed evaluation that offered nothing: every candidate in the horizon
// touched a blocking rule. Distinct from the AI empty state's copy because
// there is no generation to steer — the rules are what closed the window.
export function EngineRecommendedSlotsEmpty(): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <EngineRecommendedSlotsHeader />
      <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-gray-200 p-4 text-center">
        <p className="text-sm text-muted-foreground">
          No open slots found in the next few weeks.
        </p>
        <p className="text-xs text-muted-foreground">
          Every candidate time breaks a scheduling rule. Pick a time manually to
          book against a rule.
        </p>
      </div>
    </div>
  )
}

export interface EngineRecommendedSlotsProps {
  slots: EngineSlot[]
  // Provenance for the warning codes the slots carry; drives the badge titles.
  rules: SchedulingRuleInfoMap
  isLoading?: boolean
  selectedStart: string | undefined
  selectedEnd: string | undefined
  onAppointmentSlotSelected: (
    slot: appointments.AvailableAppointmentSlot
  ) => void
  // Reports the full candidate set so the host can draw each on the calendar
  // grid; called with [] while none are loaded and on unmount.
  onAvailableSlotsChange?: (slots: { start: string; end: string }[]) => void
}

function EngineSlotRow({
  slot,
  rules,
  selectedStart,
  selectedEnd,
  onAppointmentSlotSelected,
}: {
  slot: EngineSlot
  rules: SchedulingRuleInfoMap
  selectedStart: string | undefined
  selectedEnd: string | undefined
  onAppointmentSlotSelected: (
    slot: appointments.AvailableAppointmentSlot
  ) => void
}): JSX.Element {
  const isSelected = Boolean(
    selectedStart &&
    isSameMinute(slot.start, selectedStart) &&
    selectedEnd &&
    isSameMinute(slot.end, selectedEnd)
  )
  const titles = warningTitles(slot, rules)
  const buttonRef = useScrollIntoViewWhenSelected<HTMLButtonElement>(isSelected)

  return (
    <div className="flex items-center gap-1">
      <Button
        ref={buttonRef}
        variant="ghost"
        onClick={(evt) => {
          evt.preventDefault()
          evt.stopPropagation()
          const practitioners: Reference<Practitioner>[] = [
            { reference: `Practitioner/${slot.practitionerId}` },
          ]
          onAppointmentSlotSelected({
            id: slotKey(slot),
            period: { start: slot.start, end: slot.end },
            practitioners,
          })
        }}
        className={cn(
          'h-auto w-full flex-1 justify-start rounded-sm border border-transparent border-l-4 py-1.5 text-left font-normal text-gray-900 text-sm',
          titles.length > 0
            ? 'border-l-amber-500 bg-amber-50/60 hover:bg-amber-50'
            : 'border-l-green-600 bg-green-50/60 hover:bg-green-50',
          isSelected &&
            (titles.length > 0
              ? 'border-amber-500 bg-amber-100 font-medium hover:bg-amber-100'
              : 'border-green-600 bg-green-100 font-medium hover:bg-green-100')
        )}
      >
        {format(slot.start, 'EEE, MMM d')} · {formatTime(slot.start)} –{' '}
        {formatTime(slot.end)}
      </Button>
      {titles.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              // Non-interactive marker; the tooltip fires on hover.
              // A span (not a button) keeps it out of the tab order
              // and avoids nesting a second clickable in the row.
              role="img"
              className="flex shrink-0 items-center text-amber-600"
              aria-label="Offerable, but flagged"
            >
              <IconAlertTriangle size={14} />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            Offerable, but flagged: {titles.join(', ')}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

// Deterministic "Next Available" list from the rules engine. Visually
// interchangeable with the AI list — same row anatomy — so the flag can swap
// the two without the panel changing shape.
//
// The engine deliberately still offers candidates that overlap warning-severity
// rules, and the calendar shades those same intervals as hatched avoid bands.
// Listing both tiers together therefore reads as suggestions sitting on top of
// the hatch, so this mirrors /schedule's rail instead: clean candidates lead
// and are the only ones drawn on the grid, while warned ones stay reachable
// behind a disclosure and join the grid only once revealed.
export function EngineRecommendedSlots({
  slots,
  rules,
  isLoading = false,
  selectedStart,
  selectedEnd,
  onAppointmentSlotSelected,
  onAvailableSlotsChange,
}: EngineRecommendedSlotsProps): JSX.Element {
  const [warnedRevealed, setWarnedRevealed] = useState(false)
  const { clean, warned } = partitionEngineSlots(slots)

  // Mirror candidates up to the host, clearing on unmount so the grid does not
  // keep stale outlines after the panel closes.
  const onChangeRef = useRef(onAvailableSlotsChange)
  onChangeRef.current = onAvailableSlotsChange
  useEffect(() => {
    onChangeRef.current?.(gridMirroredSlots(slots, warnedRevealed))
  }, [slots, warnedRevealed])
  useEffect(() => () => onChangeRef.current?.([]), [])

  if (isLoading) {
    return <EngineRecommendedSlotsSkeleton />
  }

  if (!slots.length) {
    return <EngineRecommendedSlotsEmpty />
  }

  return (
    <div className="flex flex-col gap-2">
      <EngineRecommendedSlotsHeader />
      {/* Radix ScrollArea's viewport only scrolls when the root has a bounded
          height, so cap it directly here (not via a wrapper). */}
      <ScrollArea
        scrollRestorationId={null}
        className={SLOTS_VIEWPORT_CLASS}
        contentClassName={SLOTS_VIEWPORT_CLASS}
      >
        <div className="flex flex-col gap-1.5 pr-3">
          {clean.map((slot) => (
            <EngineSlotRow
              key={slotKey(slot)}
              slot={slot}
              rules={rules}
              selectedStart={selectedStart}
              selectedEnd={selectedEnd}
              onAppointmentSlotSelected={onAppointmentSlotSelected}
            />
          ))}
          {/* Times exist but every one sits on a rule the grid also shades.
              Said plainly so the disclosure below reads as the remaining
              option rather than an aside. */}
          {clean.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No conflict-free times in the next few weeks.
            </p>
          )}
          {warned.length > 0 && (
            <div className="mt-1 flex flex-col gap-1.5">
              <button
                type="button"
                onClick={(evt) => {
                  evt.preventDefault()
                  evt.stopPropagation()
                  setWarnedRevealed((revealed) => !revealed)
                }}
                aria-expanded={warnedRevealed}
                className="flex items-center gap-1 self-start text-xs text-muted-foreground hover:text-foreground"
              >
                {warnedRevealed ? (
                  <IconChevronDown size={14} />
                ) : (
                  <IconChevronRight size={14} />
                )}
                {warned.length} flagged {warned.length === 1 ? 'time' : 'times'}
              </button>
              {warnedRevealed &&
                warned.map((slot) => (
                  <EngineSlotRow
                    key={slotKey(slot)}
                    slot={slot}
                    rules={rules}
                    selectedStart={selectedStart}
                    selectedEnd={selectedEnd}
                    onAppointmentSlotSelected={onAppointmentSlotSelected}
                  />
                ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
